import { useState, useRef, useEffect, FC } from 'react';
import { ChevronDown } from 'lucide-react';
import { OptionType } from 'types';

interface SelectInputProps {
  label: string;
  name: string;
  placeholder?: string;
  options: OptionType[];
  value: OptionType | null;
  onChange: (name: string, value: OptionType | null) => void;
  error?: string;
  required?: boolean;
}

const SelectInput: FC<SelectInputProps> = ({
  label,
  name,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  error,
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close when user clicks outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (option: OptionType) => {
    onChange(name, option);
    setSearch(option.label);
    setOpen(false);
  };

  return (
    <div className="w-full mb-4" ref={dropdownRef}>
      <label className="block text-gray-500 font-medium mb-0" htmlFor={name}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Input Box */}
      <div
        className={`relative mt-1 flex items-center border ${
          error ? 'border-red-500' : 'border-gray-300'
        } rounded-lg`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <input
          type="text"
          id={name}
          name={name}
          value={search || ''}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg outline-none"
        />

        {/* Dropdown Icon */}
        <ChevronDown className="absolute right-3 text-gray-400 pointer-events-none" />
      </div>

      {/* Dropdown Menu */}
      {open && (
        <div className="mt-1 bg-white border border-gray-300 rounded-lg shadow-md max-h-40 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-gray-400 text-sm">
              No options found
            </div>
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default SelectInput;
